"use client";

/**
 * The single client-side entry point for "the user clicked Upgrade".
 *
 * Before this, exactly ONE place in the app consulted trial eligibility — the
 * 402 credit gate in app/project/[id]/page.tsx. Every other upgrade affordance
 * (~22 of them: the workspace header button, the mobile sheet, the credit chip,
 * Settings → Billing, the feature gates, the banners) opened the full-price
 * flow directly, which is why an eligible free user could only ever meet the $1
 * offer by running out of credits.
 *
 * Rather than teach 22 call sites about eligibility, they all call
 * `openUpgradeEntry` and act on the ONE surface it returns.
 *
 * ── Why there is a module-level cache ──────────────────────────────────────
 * The decision has to be effectively synchronous at click time. If we opened
 * the plans modal and swapped it for the $1 modal a beat later, that IS the
 * stacked-modal bug as the user experiences it. So eligibility is prefetched on
 * mount by <TrialOfferHost/> and cached here; a click then resolves from memory.
 *
 * A cold or unresolved cache is the one case we deliberately await — one round
 * trip before anything opens, which is what the credit gate already does
 * (needsAuthoritativeCheck). Better a short pause than the wrong modal.
 *
 * The cache is per-tab and lives as long as the page. Trial eligibility only
 * ever moves in one direction within a session (eligible → not, once they buy),
 * and every transition that matters — starting the trial, upgrading — navigates
 * to Stripe and reloads, so there is no stale-cache window to worry about.
 */

import {
  interpretTrialEligibility,
  needsAuthoritativeCheck,
  type TrialOfferState,
} from "@/lib/trial/offer-state";
import {
  resolveUpgradeEntrySurface,
  type UpgradeEntrySurface,
} from "@/lib/trial/upgrade-entry-surface";
import {
  isTrialOfferEnabled,
  type TrialSurveySource,
} from "@/lib/trial/config";
import { isCardFirstGateEnabled } from "@/lib/billing/card-first-gate";
import type { CardFirstTrialTerms } from "@/components/trial/card-first-trial-modal";
import type { FirstBuildOfferTiming } from "@/lib/trial/first-build-offer";
import {
  trackTrialEligibilityEvaluated,
  trackUpgradeEntrypointClicked,
  trialEligibilityStatus,
  type TrialEligibilityStatus,
  type UpgradeEntryPoint,
  type UpgradeEventProps,
  type UpgradeSurface,
} from "@/lib/analytics";
import type { TrialOfferCopy, TrialProjectUsage } from "@/hooks/use-trial-offer";

/** Marks every event this module emits as belonging to the PANDA-1618 routing
 *  work, so "are any PANDA-1618 routes missing events?" is one filter rather
 *  than a hand-maintained list of entry-point names. */
export const TRIAL_ROUTING_TICKET = "PANDA-1618";

/** Payload the app-wide trial host listens for. */
export interface TrialOpenDetail {
  buildLabel?: string | null;
  offer: TrialOfferCopy | null;
  surface: "t6_upgrade_entry" | "t7_first_build" | "t8_survey";
  copyVariant?: string | null;
  projects?: TrialProjectUsage | null;
  /**
   * Attribution carried to the modal (PANDA-1723).
   *
   * The modal fires trial_offer_shown / _clicked / _dismissed, and until now it
   * only knew the coarse `surface` — so all ~18 in-app buttons collapsed into
   * "t6_upgrade_entry" and "where are users dismissing the offer?" had no
   * answer. These travel WITH the dispatch because the modal is mounted at the
   * root and has no other way to learn who opened it.
   */
  entrypoint?: UpgradeEntryPoint | null;
  entrySurface?: UpgradeSurface | null;
  triggerReason?: string | null;
  eligibilityStatus?: TrialEligibilityStatus | null;
  projectId?: string | null;
  creditsRemaining?: number | null;
  /**
   * Survey attribution for the t8_survey surfaces, carried for the same reason
   * as everything above it: the modal is mounted at the root and fires
   * trial_offer_shown / _clicked / _dismissed, so a dispatch is its only route
   * to knowing which survey — and which answer — put it on screen.
   */
  surveyId?: TrialSurveySource | null;
  surveyAnswer?: string | null;
  /** Finer-grained origin than `entrypoint` (e.g. "build_quality_negative"). */
  source?: string | null;
  /**
   * How long the post-first-build offer was held back before it fired
   * (lib/trial/first-build-offer.ts). Only ever set by the t7_first_build
   * timer; every other surface leaves it undefined.
   *
   * It travels on the dispatch for the same reason the attribution above does:
   * the modal is mounted at the root and fires the shown/clicked/dismissed
   * events, so this is its only route to the numbers the timing experiment
   * needs.
   */
  offerTiming?: FirstBuildOfferTiming | null;
}

export const TRIAL_OPEN_EVENT = "panda:trial-fallback";

/**
 * Payload for the CONVERT surface — a user already inside the $1 trial.
 *
 * Sibling of TrialOpenDetail above, and deliberately separate: that event sells
 * the trial to a free user, this one sells Pro to someone already on it. They
 * are different modals, different populations, and different funnels, so one
 * event carrying a mode flag would only invite a caller to get it backwards.
 */
export interface TrialConvertDetail {
  /** Personalised headline context ("…unlock more to finish {build}"). */
  buildLabel?: string | null;
  entrypoint?: UpgradeEntryPoint | null;
  entrySurface?: UpgradeSurface | null;
  triggerReason?: string | null;
  projectId?: string | null;
  creditsRemaining?: number | null;
  /** Where to land the buyer once the conversion settles. Absent means "the
   *  entry point did not say", and the modal falls back to the page the user
   *  came from — never a hardcoded Settings → Billing, which is what sent every
   *  conversion there regardless of where it was started from. */
  returnTo?: string | null;
}

/**
 * "This trialing user pressed an upgrade CTA — show them Convert to Pro."
 *
 * Listened for by <TrialConvertHost/> in the root layout, the same way
 * TRIAL_OPEN_EVENT is listened for by <TrialDismissFallback/>. An event rather
 * than a prop for the same reason: it spares ~22 call sites — most of them in a
 * 26k-line page — from each having to mount and own a modal.
 */
export const TRIAL_CONVERT_EVENT = "panda:trial-convert";

/**
 * Payload for the CARD-FIRST surface — a free account with no payment method
 * that has never trialled (PANDA-2375).
 *
 * The third sibling of the two events above, and separate for the same reason:
 * this one is not selling a discount or a conversion, it is the wall that says
 * Panda cannot work for this account until a card exists. The terms travel with
 * the dispatch because they are the SERVER's (the same `card_first_trial` body
 * the build gate's 402 carries) — a modal that sourced its own price could
 * contradict what checkout charges.
 */
export interface CardFirstOpenDetail {
  terms: CardFirstTrialTerms;
  entrypoint?: UpgradeEntryPoint | null;
  entrySurface?: UpgradeSurface | null;
  triggerReason?: string | null;
  projectId?: string | null;
}

export const CARD_FIRST_OPEN_EVENT = "panda:card-first-trial";

let cache: TrialOfferState<TrialOfferCopy> = { status: "unknown", offer: null };
/** In-flight read, so N simultaneous callers share one request. */
let inflight: Promise<TrialOfferState<TrialOfferCopy>> | null = null;

/** Read eligibility once. Always resolves — never throws at a call site. */
export function refreshUpgradeEntryEligibility(): Promise<
  TrialOfferState<TrialOfferCopy>
> {
  // A settled no only when NEITHER trial can apply. The endpoint answers the
  // card-first question independently of the $1 offer's flag, and a deployment
  // running card-first with the legacy offer off is the expected end state — so
  // short-circuiting on that flag alone would blind every upgrade CTA to the
  // wall (PANDA-2375).
  if (!isTrialOfferEnabled() && !isCardFirstGateEnabled()) {
    // Cached so nothing re-asks for the lifetime of the page.
    cache = { status: "ineligible", offer: null, reason: "disabled" };
    return Promise.resolve(cache);
  }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      // t6_upgrade_entry: expiry-exempt (lib/trial/config EXPIRY_EXEMPT_SURFACES)
      // and outside the t2_dismiss anti-farming cap, so an eligible free user
      // meets the offer at every entry point rather than only inside the 7-day
      // window that governs the ad/email surfaces.
      const res = await fetch(
        "/api/billing/trial/eligibility?surface=t6_upgrade_entry",
      );
      const body = await res.json().catch(() => null);
      cache = interpretTrialEligibility<TrialOfferCopy>({ ok: res.ok, body });
    } catch {
      cache = { status: "unknown", offer: null, reason: "request_failed" };
    } finally {
      inflight = null;
    }
    return cache;
  })();
  return inflight;
}

/**
 * Prefetch with bounded retry. A brand-new signup lands on the app the instant
 * its session is created, so the first read often races the auth cookie and
 * comes back `no_user` — an UNKNOWN, not a no. Storing that as "ineligible" is
 * the exact bug lib/trial/offer-state.ts exists to prevent, so back off and
 * re-ask instead. Mirrors the project page's mount prefetch.
 */
export async function prefetchUpgradeEntryEligibility(
  isCancelled: () => boolean = () => false,
): Promise<void> {
  // The host is mounted in the ROOT layout, so this runs on the marketing site
  // too. Without this check every anonymous pageview would fire four retried
  // requests chasing a `no_user` that is not going to change — pure load, and
  // it would drown the real signal in the endpoint's metrics.
  if (!hasAuthCookie()) return;
  for (let attempt = 0; attempt < 4 && !isCancelled(); attempt++) {
    const state = await refreshUpgradeEntryEligibility();
    if (isCancelled() || state.status !== "unknown") return;
    await new Promise((r) => setTimeout(r, 400 * 2 ** attempt));
  }
}

/**
 * Cheap "is anyone plausibly signed in?" test.
 *
 * Supabase's SSR client stores its session in a cookie whose name is derived
 * from the project ref and always starts with `sb-`. This only has to be good
 * enough to skip work for obviously-anonymous visitors — a false positive costs
 * one request that returns `no_user`, and the real gate is the server anyway.
 */
function hasAuthCookie(): boolean {
  if (typeof document === "undefined") return false;
  return /(?:^|;\s*)sb-[^=]+=/.test(document.cookie);
}

/** What the caller knows about the account at click time. */
export interface UpgradeEntryContext {
  plan: string | null | undefined;
  /** Whether the user is inside an ACTIVE $1 trial (plan reads 'pro' for them,
   *  so this cannot be derived from `plan`). */
  trialActive?: boolean;
  growthMode?: boolean;
  hasGrowthSubscription?: boolean;
  /** Personalised headline context for the $1 modal ("Finish {build} for $1"). */
  buildLabel?: string | null;
  /** Which trigger is asking — only the three in-app surfaces are legal here. */
  surface?: TrialOpenDetail["surface"];

  // ── Attribution (PANDA-1723). REQUIRED, deliberately. ──────────────────
  //
  // Made required rather than optional so a NEW upgrade entry point cannot be
  // routed through the trial gate without declaring who it is. That is exactly
  // how the audited gap happened: PANDA-1618 added the routing to ~22 call
  // sites and 19 of them silently shipped with no attribution at all, because
  // nothing forced the question. The compiler asks it now.
  /** The exact affordance the user interacted with. */
  entrypoint: UpgradeEntryPoint;
  /** The screen it sits in. */
  entrySurface: UpgradeSurface;
  /** What prompted it: "user_click", "credit_gate", "paid_feature_request",
   *  "publish_attempt", "auto_first_build", "auto_survey", … */
  trigger?: string;
  projectId?: string | null;
  creditsRemaining?: number | null;
  /** Finer-grained origin than `entrypoint` — the bucket/variant of the same
   *  affordance (e.g. "build_quality_negative"). Fills UpgradeEventProps.source,
   *  which existed but had no way to be populated across this hop. */
  source?: string;
  /** Paid-feature key that prompted the click (lib/features/paid-features.ts). */
  featureRequested?: string;
  /** Which survey produced this, when the caller is one of the t8_survey
   *  surfaces. Travels to the modal too, so the trial funnel's exposure /
   *  click / dismissal rows name the same survey as the decision row. */
  surveyId?: TrialSurveySource;
  /** What the user answered in that survey immediately before the offer. */
  surveyAnswer?: string | null;
  /** Timing for an auto-dispatched post-first-build offer, forwarded to both
   *  the decision event and the modal. Set only by that one caller. */
  offerTiming?: FirstBuildOfferTiming | null;
  /**
   * The caller fires its OWN `upgrade_entrypoint_clicked` (with the decision
   * merged in) — so this module must not fire a second one. Set at the handful
   * of sites that already had a click event before this audit; without it they
   * would double-count and quietly corrupt existing dashboards.
   */
  alreadyTracked?: boolean;
  /**
   * The app raised this on its own initiative (the post-first-build timer, the
   * chat thumbs-up) — there was no upgrade CTA and no click, so no
   * `upgrade_entrypoint_clicked` may be emitted. It would inflate click counts
   * on every existing upgrade dashboard with impressions nobody asked for.
   * The decision event still fires.
   */
  autoDispatched?: boolean;
}

export interface UpgradeEntryDecision {
  surface: UpgradeEntrySurface;
  /** True when this call already opened the $1 modal and the caller must NOT
   *  open its own paywall — the "never two money asks" rule. */
  handled: boolean;
  /** Whether the account was trial-eligible, for the funnel. */
  eligible: boolean;
  /** The decision in the funnel's own vocabulary (PANDA-1723). */
  eligibilityStatus: TrialEligibilityStatus;
  /** Raw server reason behind it, for debugging. */
  eligibilityReason: string | null;
  /**
   * Ready-made analytics properties for a caller that fires its own
   * `upgrade_entrypoint_clicked`. Handing back the exact object this module
   * used means the caller's event and the decision event can never disagree
   * about the same click — which is the failure the audit found at the three
   * sites that DID track, where each re-derived its own subset by hand.
   */
  analyticsProps: UpgradeEventProps;
}

/**
 * Resolve — and, for the trial case, OPEN — the right surface for this click.
 *
 * Returns `handled: true` only when the $1 modal was dispatched. In every other
 * case the caller opens its own existing flow, unchanged, using `surface` to
 * pick between choose-a-plan / add-credits / plan management.
 */
export async function openUpgradeEntry(
  ctx: UpgradeEntryContext,
): Promise<UpgradeEntryDecision> {
  const flagEnabled = isTrialOfferEnabled();
  const cardFirstEnabled = isCardFirstGateEnabled();

  // Nothing this account's eligibility could say would change the answer, so
  // don't make the user's click wait on a network read to find that out. A paid
  // user, a user mid-trial, and a Grow-mode click can all never resolve to
  // "trial" (see resolveUpgradeEntrySurface), and this is the exact population
  // most likely to meet a cold cache — they rarely see trial surfaces, so
  // nothing else has warmed it.
  const cannotBeTrial =
    (!flagEnabled && !cardFirstEnabled) ||
    ctx.growthMode === true ||
    ctx.trialActive === true ||
    (typeof ctx.plan === "string" && ctx.plan !== "" && ctx.plan !== "free");

  // Otherwise only spend a round trip when the answer is genuinely unknown. An
  // eligible user already has their offer and a settled no will not change by
  // asking twice — so the common paths stay fast.
  let state = cache;
  if (!cannotBeTrial && needsAuthoritativeCheck(state)) {
    state = await refreshUpgradeEntryEligibility();
  }

  const eligible = state.status === "eligible";
  const surface = resolveUpgradeEntrySurface({
    plan: ctx.plan,
    trialActive: Boolean(ctx.trialActive),
    trialFlagEnabled: flagEnabled,
    trialEligible: eligible,
    trialOfferPresent: Boolean(state.offer),
    cardFirstRequired: state.cardFirst?.required === true,
    growthMode: ctx.growthMode,
    hasGrowthSubscription: ctx.hasGrowthSubscription,
  });

  // The flag being off is a real, reportable reason for not offering the trial
  // — and it is the FIRST thing to check when QA says "the offer never
  // appears". The cache stores it as reason "disabled" only when the flag is
  // off at prefetch time; a click that short-circuits via `cannotBeTrial` never
  // touches the cache, so name it here.
  const reason = !flagEnabled
    ? "disabled"
    : ctx.growthMode
      ? "growth_mode"
      : ctx.trialActive
        ? "trial_active"
        : (state.reason ?? null);

  const eligibilityStatus: TrialEligibilityStatus =
    // A Grow-mode click is out of the $1 trial's scope entirely — the offer
    // only ever converts to Build's Pro. Classified explicitly rather than
    // left to the mapper, which would see a free-plan user with no server
    // reason and report "unknown": a measurement gap that does not exist.
    ctx.growthMode && !ctx.trialActive
      ? "ineligible"
      : trialEligibilityStatus({
          status: eligible ? "eligible" : state.status,
          reason,
          plan: ctx.plan,
          trialActive: ctx.trialActive,
        });

  const trialSurface = ctx.surface ?? "t6_upgrade_entry";
  const willOpenTrial = surface === "trial" && Boolean(state.offer);

  /**
   * The CONVERT surface: this user is already inside the $1 trial.
   *
   * This is the bug this block exists to fix. `plan_management` was resolved
   * correctly all along and then DISCARDED, because `handled` was true only for
   * the "trial" surface. Every call site reads `if (handled) return;` and
   * otherwise opens the credit/plans paywall — which is 403'd for the entire
   * trial (lib/trial/purchase-guard.ts), so a trialing user was routed to a buy
   * screen that could not buy. Acting on the decision HERE fixes all ~22 entry
   * points at once, and means a new entry point cannot reintroduce it.
   *
   * All three conditions are load-bearing:
   *
   *  • `trialActive` — the population. A trial row is plan='pro', so the plan
   *    alone cannot identify it; this flag is the only thing that can. It means
   *    trial_status === 'active' at every source in the codebase, which is what
   *    keeps a DUNNING trial (trial_status 'past_due') out: that user keeps Pro
   *    access while Stripe retries and needs their card-update path, not a
   *    convert modal for a conversion already attempted. Converted, cancelled
   *    and expired trials are all excluded by the same test.
   *
   *  • `surface === "plan_management"` — belt and braces. Today `trialActive`
   *    always resolves to it in Build mode, but pinning the surface means a
   *    future resolver change cannot silently re-route this population without
   *    the tests noticing.
   *
   *  • `!growthMode` — Grow is a separate subscription ladder that the resolver
   *    decides BEFORE any trial reasoning, and the $1 trial only ever converts
   *    to Build's Pro. Not hypothetical: trial fulfilment writes a
   *    stripe_subscription_id, so `hasGrowthSubscription` is TRUE for every
   *    trial account — a trialing user who toggled Grow mode would otherwise
   *    satisfy `plan_management` and have their Grow picker replaced by a
   *    Build-tier conversion. A Grow-mode click is left exactly as it is today.
   *
   *  • `!autoDispatched` — the ticket is about upgrade CTAs the user PRESSED.
   *    Two callers dispatch with no click behind them: the post-first-build
   *    timer (10s after a build finishes) and the chat thumbs-up. Those exist to
   *    offer a FREE user the $1 trial; answering them for a trialing user would
   *    put an unrequested payment modal over the app they just built, and again
   *    on every thumbs-up. A conversion ask has to be something the user asked
   *    for.
   */
  const willOpenConvert =
    !willOpenTrial &&
    ctx.trialActive === true &&
    surface === "plan_management" &&
    ctx.growthMode !== true &&
    ctx.autoDispatched !== true;

  // ONE properties object, built once and shared by every event this call
  // emits (and handed back to the caller). Two events describing the same
  // click must never disagree.
  const analyticsProps: UpgradeEventProps = {
    entry_point: ctx.entrypoint,
    surface: ctx.entrySurface,
    trigger: ctx.trigger ?? (ctx.autoDispatched ? "auto" : "user_click"),
    project_id: ctx.projectId ?? null,
    plan_before: ctx.plan ?? null,
    credits_remaining: ctx.creditsRemaining ?? null,
    is_trial_eligible: eligible,
    trial_offer_shown: willOpenTrial,
    resolved_surface: surface,
    eligibility_status: eligibilityStatus,
    eligibility_reason: reason,
    // Only meaningful when the trial actually opened; null elsewhere keeps the
    // join key honest instead of implying an exposure that never happened.
    trial_surface: willOpenTrial ? trialSurface : null,
    copy_variant: state.copyVariant,
    source_ticket: TRIAL_ROUTING_TICKET,
    // Survey attribution rides the DECISION row too, not only the exposure.
    // Two of the four t8_survey dispatchers are auto-dispatched, which
    // deliberately suppresses upgrade_entrypoint_clicked — so for those,
    // trial_eligibility_evaluated is the only row that always fires and the
    // only place their source can reliably be read.
    source: ctx.source,
    feature_requested: ctx.featureRequested,
    // Emitted as a PAIR or not at all: a `survey_answer` with no `survey_id`
    // names an answer to an unknown question, and a null `survey_id` on every
    // non-survey upgrade row is noise on the app's highest-volume events.
    ...(ctx.surveyId
      ? { survey_id: ctx.surveyId, survey_answer: ctx.surveyAnswer ?? null }
      : {}),
    // Set on EVERY outcome, not only when the trial opens: an offer we waited
    // 35s for and then routed to the plans picker is exactly the row that says
    // whether the wait cost us anything.
    offer_timing: ctx.offerTiming ?? null,
  };

  // Every decision, always — eligible or not, trial or not. This is the row
  // that makes the routing fallback measurable: an ineligible user sent to the
  // regular plan picker is `resolved_surface: "choose_plan"` here, which the
  // ticket asks to be "tracked distinctly".
  trackTrialEligibilityEvaluated(analyticsProps);

  // The click itself, for the sites that had no click event before this audit.
  // Skipped for callers that fire their own (double-count) and for surfaces the
  // app raised on its own initiative (there was no click to report).
  if (!ctx.alreadyTracked && !ctx.autoDispatched) {
    trackUpgradeEntrypointClicked(analyticsProps);
  }

  // Card-first → the wall, and nothing else. This account cannot spend, top up
  // or upgrade its way past a missing payment method, so every other surface
  // this function can return would be a dead end for it. Dispatched here rather
  // than at the call sites so all ~20 upgrade entry points open the SAME modal
  // the build gate opens, from the same server terms (PANDA-2375).
  const cardFirstTerms = state.cardFirst?.terms as CardFirstTrialTerms | undefined;
  if (surface === "card_first" && cardFirstTerms) {
    const cardDetail: CardFirstOpenDetail = {
      terms: cardFirstTerms,
      entrypoint: ctx.entrypoint,
      entrySurface: ctx.entrySurface,
      triggerReason: analyticsProps.trigger ?? null,
      projectId: ctx.projectId ?? null,
    };
    window.dispatchEvent(
      new CustomEvent(CARD_FIRST_OPEN_EVENT, { detail: cardDetail }),
    );
    return {
      surface,
      // Same contract as the two branches below: a money surface is open, the
      // caller must not open its own.
      handled: true,
      eligible,
      eligibilityStatus,
      eligibilityReason: reason,
      analyticsProps,
    };
  }

  // Trialing user → Convert to Pro, and nothing else. Dispatched BEFORE the
  // `!willOpenTrial` bail below, which is the line that used to drop this
  // population through to the caller's paywall.
  if (willOpenConvert) {
    const convertDetail: TrialConvertDetail = {
      buildLabel: ctx.buildLabel ?? null,
      entrypoint: ctx.entrypoint,
      entrySurface: ctx.entrySurface,
      triggerReason: analyticsProps.trigger ?? null,
      projectId: ctx.projectId ?? null,
      creditsRemaining: ctx.creditsRemaining ?? null,
    };
    window.dispatchEvent(
      new CustomEvent(TRIAL_CONVERT_EVENT, { detail: convertDetail }),
    );
    return {
      surface,
      // The contract `handled` has always carried: "a money surface is now open,
      // do NOT open yours". Callers already honour it verbatim, which is why
      // this needs no change at any of them.
      handled: true,
      eligible,
      eligibilityStatus,
      eligibilityReason: reason,
      analyticsProps,
    };
  }

  if (!willOpenTrial) {
    return {
      surface,
      handled: false,
      eligible,
      eligibilityStatus,
      eligibilityReason: reason,
      analyticsProps,
    };
  }

  // Hand the ALREADY-RESOLVED offer to the modal rather than letting it run a
  // second, independent eligibility check. A re-check can diverge from this one
  // — and when it does, the caller has already declined to open its paywall, so
  // the user is left with no modal at all.
  const detail: TrialOpenDetail = {
    buildLabel: ctx.buildLabel ?? null,
    offer: state.offer,
    surface: trialSurface,
    copyVariant: state.copyVariant ?? null,
    projects: state.projects ?? null,
    // Attribution travels with the dispatch — the modal is mounted at the root
    // and has no other way to learn which button opened it.
    entrypoint: ctx.entrypoint,
    entrySurface: ctx.entrySurface,
    triggerReason: analyticsProps.trigger ?? null,
    surveyId: ctx.surveyId ?? null,
    surveyAnswer: ctx.surveyAnswer ?? null,
    source: ctx.source ?? null,
    eligibilityStatus,
    projectId: ctx.projectId ?? null,
    creditsRemaining: ctx.creditsRemaining ?? null,
    offerTiming: ctx.offerTiming ?? null,
  };
  window.dispatchEvent(new CustomEvent(TRIAL_OPEN_EVENT, { detail }));
  return {
    surface: "trial",
    handled: true,
    eligible,
    eligibilityStatus,
    eligibilityReason: reason,
    analyticsProps,
  };
}

/**
 * Drop the cached answer.
 *
 * The cache is what makes the click synchronous, but it is also a claim about
 * the account that can go stale: the user might upgrade in another tab, or take
 * the trial and come back with the browser's back button. A stale "eligible"
 * would show the $1 modal to someone the server will then refuse — a modal that
 * looks broken. Re-reading on tab focus is cheap and closes that window.
 */
export function invalidateUpgradeEntryEligibility(): void {
  cache = { status: "unknown", offer: null };
}

/** Test seam — reset the module cache between cases. */
export function __resetUpgradeEntryCacheForTests(): void {
  cache = { status: "unknown", offer: null };
  inflight = null;
}
